<template>
  <div class="ui compact menu">
    <div class="header">{{ $tc('ACTION', 2) }}</div>
    <router-link class="item" :to="{ name: 'new-flow' }">
      <i class="plus icon"></i>
      {{ $t('NEW', { thing: $tc('FLOW', 1) }) }}
    </router-link>
    <router-link class="item" :to="{ name: 'download-flow' }" v-if="hubs && hubs.length > 0">
      <i class="download icon"></i>
      {{ $t('GET', { thing: $tc('FLOW', 1) }) }}
    </router-link>
    <div class="divider"></div>
    <!-- Filter -->
    <div class="header" v-if="showFlowFilters">
      {{ $t('FILTER_BY', { thing: $tc('GROUP', 2) }) }}
    </div>
    <filter-by
      class="item"
      v-if="showFlowFilters"
      :items="groups"
      :current-items="filterGroups"
      :placeholder-text="$tc('GROUP', 1)"
      :empty-text="$t('NO_ITEMS_MATCH', { things: $tc('GROUP', 2) })"
      :select-handler="onGroupFilterAdd"
      :remove-handler="onGroupFilterRemove"
      :useColors="true"
    />
    <div class="header" v-if="showFlowFilters">
      {{ $t('FILTER_BY', { thing: $tc('MAINTAINER', 2) }) }}
    </div>
    <filter-by
      class="item"
      v-if="showFlowFilters"
      :items="users"
      :current-items="filterMaintainers"
      :placeholder-text="$tc('MAINTAINER', 1)"
      :empty-text="$t('NO_ITEMS_MATCH', { things: $tc('MAINTAINER', 2) })"
      :select-handler="onMaintainerFilterAdd"
      :remove-handler="onMaintainerFilterRemove"
    />

    <div class="divider" v-if="showFlowFilters" />

    <!-- Search -->
    <div class="header" v-if="showFlowFilters">
      {{ $t('SEARCH', { thing: $tc('FLOW', 2) }) }}
    </div>
    <div class="ui icon input" v-if="showFlowFilters">
      <i class="search icon"></i>
      <input
        type="text"
        name="flowSearch"
        :placeholder="$t('SEARCH')"
        v-model="searchTerm"
        @keydown.esc="searchTerm = ''"
        autocomplete="off"
      />
    </div>
    <div class="divider" v-if="showFlowFilters"></div>
    <!-- Flows -->
    <div class="header capitalize-fl">
      {{ $tc('FLOW', flows.length) }}
      <span>({{ filteredFlows.length }}/{{ flows.length }})</span>
    </div>
    <div class="scroller fuzzy-list">
      <Spinny :promise="flowsPromise" />
      <router-link
        class="item"
        :to="{
          name: 'app-flow',
          params: {
            appId: app.id,
            flowId: flow.original.id,
            mode: 'auto'
          }
        }"
        :active-class="'selected'"
        v-for="flow in limitedFilteredFlows"
        :key="flow.original.id"
      >
        <div class="scrollerItem">
          <i class="icon flowIcon" :class="getFlowTypeIcon(flow)" />
          <div class="ellipsis flowName" v-html="flow.string" :title="flow.string"></div>
          <multi-color-label
            class="groupIndicator"
            size="tiny"
            :colors="flow.original.groupsColors"
          />
        </div>
      </router-link>
    </div>
  </div>
</template>
<style lang="scss" scoped>
@charset "utf-8";
@import '../../scss/variables';
// Note: this component is partially styled by its parent, ApplicationMenu

.flowIcon {
  flex: none;
}

.scrollerItem {
  line-height: 1.1em;
}

.flowName {
  flex: auto;
  margin-left: 10px;
}

.groupIndicator {
  flex: none;
  margin-left: 10px;
}
</style>
<script>
// @flow
import { FilterBy, Spinny, MultiColorLabel } from 'ui'
import { Flow, Group, User, Hub } from 'services'
import { MutationTypes } from 'store'
import FuzzyFilterer from '../Mixins/FuzzyFilterer'

const MAX_FLOWS_TO_SHOW = 100

// Note: this component is tied quite hard to its parent. The reason for the split is that it is too expensive to maintain
// the flow menu while typing in a flow. So the flow menu now only exists while it is hovered (v-if)
export default {
  name: 'flow-menu',
  components: { FilterBy, Spinny, MultiColorLabel },
  props: ['app', 'groupColorsByFlow', 'filterGroups', 'filterMaintainers'],
  mixins: [FuzzyFilterer],
  data() {
    return {
      flowsPromise: null,
      hubsPromise: null
    }
  },

  computed: {
    // Override computed in fuzzy mixin
    searchTerm: {
      get() {
        return this.$store.state.flowSearchTerm || ''
      },
      set(v) {
        this.$store.commit(MutationTypes.SET_FLOW_SEARCH_TERM, v)
      }
    },
    flows() {
      return this.app.id ? this.$store.getters['flow/getItemsForApp'](this.app.id) : []
    },
    groups() {
      return this.$store.state.group.items
    },
    users() {
      return this.$store.state.user.items
    },
    hubs() {
      return this.$store.state.hub.items
    },
    limitedFilteredFlows() {
      const res = _.take(this.filteredFlows, MAX_FLOWS_TO_SHOW)
      if (res.length < this.filteredFlows.length)
        res.push({
          string: this.$t('TOO_MANY_FLOWS_TO_SHOW'),
          original: {
            type: 'overflow',
            name: this.$t('TOO_MANY_FLOWS_TO_SHOW'),
            groupColors: []
          }
        })
      return res
    },
    filteredFlows() {
      const filterGroupsFlows = _.flatMap(this.filterGroups, g => g.flow || [])
      return this.flowsByMaintainer.filter(
        flow =>
          !this.filterGroups ||
          this.filterGroups.length === 0 ||
          _.includes(filterGroupsFlows, flow.original.id)
      )
    },
    flowsByMaintainer() {
      const maintainersIds = this.filterMaintainers.map(m => m.id)
      return this.filterMaintainers && this.filterMaintainers.length > 0
        ? this.flowsBySearchTerm.filter(flow =>
            _.includes(maintainersIds, flow.original.maintainer)
          )
        : this.flowsBySearchTerm
    },
    flowsWithGroupColors() {
      return _.map(this.flows, flow => {
        return { ...flow, groupsColors: this.groupColorsByFlow[flow.id] || [] }
      })
    },
    // Specify full list for fuzzy mixin
    items() {
      return this.flowsWithGroupColors
    },
    flowsBySearchTerm() {
      return this.filteredItems
    },
    showFlowFilters() {
      return this.flows && this.flows.length > 10
    }
  },

  methods: {
    getFlowTypeIcon(flow) {
      const published = !_.isEmpty(flow.original.publication)
      const flowType = flow.original.type
      if (flowType === 'overflow') return 'yellow exclamation triangle'
      return (published ? 'blue ' : '') + Flow.flowTypeIcons[flowType]
    },

    onGroupFilterAdd(group) {
      this.$emit('onGroupFilterAdd', group)
    },

    onGroupFilterRemove(group) {
      this.$emit('onGroupFilterRemove', group)
    },

    onMaintainerFilterAdd(maintainer) {
      this.$emit('onMaintainerFilterAdd', maintainer)
    },

    onMaintainerFilterRemove(maintainer) {
      this.$emit('onMaintainerFilterRemove', maintainer)
    }
  },

  created() {
    User.readAllUsers()
    Group.readAllGroups()
    this.flowsPromise = Flow.readAllFlows()
    Hub.list()
  }
}
</script>
